import { useContext, useEffect, useRef, useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AuthContext } from "../components/AuthContextComponent";
import { useCustomAccount } from "../components/CustomAccountContext";
import { STATUS } from "../lib/enums";
import { ReportsRedirection } from "../api/FetchAPI";
import { showAlert } from "../promptMessages/PromptMessages";
import { useBalance } from "../components/BalanceContext";
import { handleHttpError } from "../lib/helpers/HanldeHttpError";

const useHeadComponent = (selectedPartner?: number) => {
  const { userInfo, prefundInfo, username } = useContext(AuthContext);
  const { customAccount } = useCustomAccount();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { balances, fetchBalance } = useBalance();

  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  const themeBackground = userInfo?.theme === "DARK" ? "#24292e" : "";
  const themeTextColor = userInfo?.theme === "DARK" ? "#fafbfc" : "";

  const customColor =
    customAccount?.customAccountInfo[0].themeCode.toLowerCase() || "";

  const partnerId = useMemo(() => {
    if (userInfo?.category === STATUS.INTERNAL) return selectedPartner;
    return userInfo?.partnersId ? Number(userInfo.partnersId) : undefined;
  }, [selectedPartner, userInfo?.category, userInfo?.partnersId]);

  useEffect(() => {
    const shouldFetch = (() => {
      if (!partnerId) return false;
      if (userInfo?.category === STATUS.INTERNAL)
        return pathname === "/paymentsolution/manual-upload";
      return pathname !== "/";
    })();

    if (shouldFetch && partnerId && balances[partnerId] === undefined) {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(() => {
        fetchBalance(partnerId);
      }, 300); // Debounce for 300ms
    }

    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [partnerId, pathname, userInfo?.category, balances, fetchBalance]);

  const handleReportsRedirection = useCallback(async () => {
    try {
      const response = await ReportsRedirection(username);
      if (response.respCode === 2000) {
        window.open(`https://${response.redirectURL}/`, "_blank");
      } else {
        showAlert(
          "Something went wrong!",
          response.respMsg,
          "error",
          customColor,
        );
      }
    } catch (error) {
      // showAlert(
      //   "Something went wrong!",
      //   "Failed to redirect",
      //   "error",
      //   customColor
      // );
      handleHttpError(error, customColor);
    }
  }, [username, customColor]);

  const handleTransactionPagesRedirection = useCallback(
    (value: string) => {
      navigate(value);
    },
    [navigate],
  );

  return {
    img: sessionStorage.getItem("favicon") || "",
    bannerImg: sessionStorage.getItem("bannerImg") || "",
    Balance: partnerId && balances[partnerId] ? balances[partnerId] : "N/A",
    prefundInfo,
    customBannerImg: "",
    partnerBalance: (overrideId?: number) => {
      const targetId = overrideId || partnerId;
      if (targetId) fetchBalance(targetId, customColor, true);
    },
    handleReportsRedirection,
    handleTransactionPagesRedirection,
  };
};

export default useHeadComponent;
